import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Button,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  FormHelperText,
  IconButton,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import * as Yup from "yup";
import { debounce } from "lodash";
import { useGetLocalAndLenderErrors } from "@/hooks/useRequiredFields/useGetLocalAndLenderErrors";
import { getShouldValidatePersonalFinanceItem } from "@/hooks/useRequiredFields/utils/requiredFieldValues";
import regex from "../../utils/regex";
import { assetTypesList, ERRORS_MESSAGE, validCustomerAssetType } from "../../constants";
import { NumericFormatCustom, currencyMaskedValue } from "../../utils/currencyMaskFormat";
import {
  saveValue,
  createAssets,
  deleteAssets,
  updateAssets,
  userDetailsSelector,
  setRequiredFieldsErrors,
} from "../../store/slices/applicationFormSlice";
import { customerSelector } from "../../store/slices/customerSlice";
import { openSnackbar } from "../../store/slices/snackbarSlice";
import { getErrorToastPayload } from "../../utils/commonFunctions";

export default function Assets({ assets, customerId, applicationId, personalFinLoaders, isDisableEdit }) {
  const dispatch = useDispatch();
  const { isCustomerLogin = false } = useSelector(customerSelector);
  const isDisable = isCustomerLogin ? false : isDisableEdit;
  const [assetList, setAssetList] = useState(assets || []);
  const { requireFieldErrors } = useSelector(userDetailsSelector);
  const [changeDetected, setChangeDetected] = useState(false);
  const isAddAssetLoader = personalFinLoaders?.addAssetLoader && personalFinLoaders?.id === customerId;

  useEffect(() => {
    setAssetList(assets || []);
  }, [assets]);

  const validationSchema = Yup.object({
    assetType: Yup.string()
      .oneOf(validCustomerAssetType, "Please select valid asset type")
      .required("Asset type is required"),
    value: Yup.string()
      .optional()
      .matches(regex.allowOnlyNumber, "Only numbers allowed.")
      .max(10, "Maximum of 7 digits"),
    description: Yup.string().required("Description is required"),
  });

  const debouncedValidation = useCallback(
    debounce(async (fieldName, value) => {
      try {
        const field = fieldName.split("_")[0];
        await validationSchema.validateAt(field, { [field]: value });
        dispatch(
          setRequiredFieldsErrors({
            ...requireFieldErrors,
            [fieldName]: "",
          }),
        );
      } catch (error) {
        dispatch(
          setRequiredFieldsErrors({
            ...requireFieldErrors,
            [fieldName]: error.message,
          }),
        );
      }
    }, 300),
    [requireFieldErrors],
  );

  const { getCustomerOrEntityLocalAndLenderError } = useGetLocalAndLenderErrors();
  const getLocalAndLenderError = ({ fieldError, fieldName, localFieldName, doNotShowError }) =>
    getCustomerOrEntityLocalAndLenderError({ fieldError, localState: {}, fieldName, localFieldName, doNotShowError });
  // Lender errors only show on validated rows: the first row and any row holding data.
  const validatedRows = assetList.map((item, index) =>
    getShouldValidatePersonalFinanceItem({ listName: "customerAssets", item, index }),
  );

  const onAddNewAssetType = () => {
    const data = {
      assetType: "",
      value: "",
      description: "",
      application: applicationId,
      customer: customerId,
    };
    dispatch(
      saveValue({
        personalFinLoaders: {
          ...personalFinLoaders,
          id: customerId,
        },
      }),
    );
    dispatch(createAssets({ data }));
  };

  const removeAssetType = (Element) => {
    const data = {
      assetId: Element._id,
      application: applicationId,
      customer: Element.customer,
    };
    delete data._id;
    dispatch(
      saveValue({
        personalFinLoaders: {
          ...personalFinLoaders,
          id: Element._id,
        },
      }),
    );
    dispatch(deleteAssets({ data })).then((res) => {
      if (res?.error) {
        dispatch(openSnackbar(getErrorToastPayload(res, ERRORS_MESSAGE.fetchErrorMsg)));
      }
    });
  };

  const handle = {
    onBlur: (name, value, newID) => {
      const temp = [...assetList];

      temp.map((ele) => {
        if (ele._id === newID) {
          let updatedAssetData = {
            ...ele,
            [name]: value,
            assetId: newID,
            application: applicationId,
            customer: customerId,
          };
          delete updatedAssetData._id;

          if (changeDetected) {
            dispatch(updateAssets({ data: updatedAssetData }));
            setChangeDetected(false);
          }
          return updatedAssetData;
        } else {
          return ele;
        }
      });
    },
    onChange: async (field, value, newID, index) => {
      const name = field.split("_")[0];
      debouncedValidation(field, value);

      const temp = [...assetList];
      let isChangeDetected = false;
      const updateAssetType = temp.map((ele) => {
        if (!ele._id) {
          const dataWithOutId = {
            ...ele,
            [name]: value,
            application: applicationId,
            customer: customerId,
          };
          isChangeDetected = true;
          dispatch(createAssets({ data: dataWithOutId }));
        }
        if (ele._id === newID) {
          const updatedAssetsItem = {
            ...ele,
            [name]: value,
          };

          if (ele[name] !== value) {
            isChangeDetected = true;
          }
          return updatedAssetsItem;
        } else {
          return ele;
        }
      });

      setChangeDetected(isChangeDetected);
      if (isChangeDetected) {
        setAssetList(updateAssetType);
      }
    },
  };

  return (
    <>
      <Grid xs={12}>
        <Typography variant="subtitle2" sx={{ fontWeight: "bold" }} style={{ marginBottom: "5px", marginTop: "20px" }}>
          Assets
        </Typography>
      </Grid>
      {assetList?.map((ele, index) => (
        <Grid container xs={12} spacing={1} mb={"20px"} key={ele?._id}>
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth variant="filled">
              <InputLabel
                id={`asset-type-label-${ele?._id}`}
                error={getLocalAndLenderError({
                  fieldName: `customerAssets[0].assetType_${customerId}_${index}`,
                  doNotShowError: !!ele?.assetType || !validatedRows[index],
                })}
              >
                Asset Type
              </InputLabel>
              <Select
                size="small"
                labelId={`asset-type-label-${ele?._id}`}
                id={`asset-type-${ele?._id}`}
                value={ele?.assetType}
                label="Asset Type"
                name={`assetType_${customerId}_${index}`}
                disabled={isDisable}
                onChange={(event) => {
                  handle.onChange(`assetType_${customerId}_${index}`, event.target.value, ele?._id, index);
                }}
                onBlur={(event) => handle.onBlur("assetType", ele?.assetType, ele?._id, index)}
                error={getLocalAndLenderError({
                  fieldName: `customerAssets[0].assetType_${customerId}_${index}`,
                  doNotShowError: !!ele?.assetType || !validatedRows[index],
                })}
              >
                {assetTypesList?.map((i, index) => (
                  <MenuItem key={index} value={i.value}>
                    {i.label}
                  </MenuItem>
                ))}
              </Select>
              {getLocalAndLenderError({
                fieldName: `customerAssets[0].assetType_${customerId}_${index}`,
                doNotShowError: !!ele?.assetType || !validatedRows[index],
              }) && (
                <FormHelperText sx={{ color: "#d32f2f" }}>
                  {getLocalAndLenderError({
                    fieldName: `customerAssets[0].assetType_${customerId}_${index}`,
                    doNotShowError: !!ele?.assetType || !validatedRows[index],
                  })}
                </FormHelperText>
              )}
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              size="small"
              id={`asset-value-${ele?._id}`}
              type="text"
              label="Value"
              variant="filled"
              fullWidth
              disabled={isDisable}
              value={ele?.value}
              InputProps={{
                inputComponent: NumericFormatCustom,
              }}
              name={`value_${customerId}_${index}`}
              onChange={(event) => handle.onChange(`value_${customerId}_${index}`, event.target.value, ele?._id, index)}
              onBlur={(event) => handle.onBlur("value", currencyMaskedValue(ele?.value), ele?._id, index)}
              error={getLocalAndLenderError({
                fieldName: `customerAssets[0].value_${customerId}_${index}`,
                doNotShowError: !!ele?.value || !validatedRows[index],
              })}
              helperText={getLocalAndLenderError({
                fieldName: `customerAssets[0].value_${customerId}_${index}`,
                doNotShowError: !!ele?.value || !validatedRows[index],
              })}
            />
          </Grid>
          <Grid item xs={12} sm={5}>
            <TextField
              size="small"
              id={`asset-description-${ele?._id}`}
              type="text"
              label="Description"
              variant="filled"
              disabled={isDisable}
              style={{ width: "100%" }}
              value={ele?.description}
              name={`description_${customerId}_${index}`}
              onChange={(event) =>
                handle.onChange(`description_${customerId}_${index}`, event?.target?.value, ele?._id, index)
              }
              onBlur={(event) => handle.onBlur("description", ele?.description, ele?._id)}
              error={getLocalAndLenderError({
                fieldName: `customerAssets[0].description_${customerId}_${index}`,
                doNotShowError: !!ele?.description || !validatedRows[index],
              })}
              helperText={getLocalAndLenderError({
                fieldName: `customerAssets[0].description_${customerId}_${index}`,
                doNotShowError: !!ele?.description || !validatedRows[index],
              })}
            />
          </Grid>

          <Grid item xs={0.5}>
            <IconButton
              size="small"
              aria-label={`Remove asset ${index + 1}`}
              title={
                personalFinLoaders?.removeAssetLoader && personalFinLoaders?.id === ele?._id
                  ? "Removing..."
                  : "Remove asset"
              }
              sx={{ marginTop: "10px", color: "error.main" }}
              onClick={() => removeAssetType(ele)}
              disabled={isDisable || personalFinLoaders?.removeAssetLoader}
            >
              <DeleteOutlineIcon fontSize="medium" />
            </IconButton>
          </Grid>
        </Grid>
      ))}
      <Grid item xs={12}>
        <div>
          {/* Allow for adding first asset when the asset is an empty list or is undefined. */}
          <Button
            size="small"
            onClick={() => onAddNewAssetType()}
            variant="outlined"
            disabled={
              isAddAssetLoader ||
              isDisable ||
              (assetList?.length > 0 &&
                !assetList?.[0]?.assetType &&
                !assetList?.[0]?.value &&
                !assetList?.[0]?.description)
            }
          >
            {isAddAssetLoader ? "Adding..." : "Add asset"}
          </Button>
        </div>
      </Grid>
    </>
  );
}
